import React, { useState, useEffect } from "react";
import { Modal, Input, Select, Switch, Button, notification, Popover, Checkbox } from "antd";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import { createTags, updateTagV2 } from "../../../../Catalog/_redux/tags/tagsCrud";
import { handleApiErrorResponse } from "../../../../Common/utils";
import ReactGPicker from "react-gcolor-picker";

const { TextArea } = Input;
const { Option } = Select;

const validationSchema = Yup.object().shape({
  value: Yup.string().required("Label name is required"),
  tag_type: Yup.number().required("Label type is required"),
});

const initialValues = {
  value: "",
  tag_type: undefined,
  color: "#000000",
  bg_color: "#ffffff",
  status: true,
  description: "",
  show_on_fulfillment: false,
  show_on_invoice: false,
};

const CreateTags = ({ open, onClose, setRefresh, item, onSuccess, defaultTagType, defaultValues, getContainer }) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [BgColorPickerOpen, setBgColorPickerOpen] = useState(false);

  useEffect(() => {
    if (item) {
      setFormValues({
        value: item.value || "",
        tag_type: item.tag_type,
        color: item.color || "#000000",
        bg_color: item.bg_color || "#ffffff",
        status: item.status === 1,
        description: item.description || "",
        show_on_fulfillment: item.external_details?.show_on_fulfillment === true,
        show_on_invoice: item.external_details?.show_on_invoice === true,
      });
    } else {
      setFormValues({ ...initialValues, ...defaultValues, tag_type: defaultTagType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultValues is an inline object; read once per open.
  }, [item, defaultTagType]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        ...values,
        status: values.status ? 1 : 0,
      };

      if (values.tag_type !== 1) {
        delete payload.show_on_fulfillment;
        delete payload.show_on_invoice;
      }
      let response;

      if (item?.id) {
        response = await updateTagV2(item.id, payload);
        notification.success({
          message: "Label Updated",
          description: response?.data?.message,
        });
      } else {
        response = await createTags(payload);
        notification.success({
          message: "Label Created",
          description: response?.data?.message,
        });
      }

      resetForm();
      onClose();
      if(onSuccess){
        onSuccess?.(); 
      }
      setRefresh && setRefresh((prev) => prev + 1);
    } catch (error) {
      handleApiErrorResponse(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={<span style={{ fontSize: 18, fontWeight: 500 }}>{item?.id ? "Edit Label" : "Create Label"}</span>}
      visible={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      className="tag-modal"
      // Inside another (Bootstrap) modal, render there so its focus trap lets the inputs take focus.
      getContainer={getContainer}
      maskClosable={false} 
    >
      <Formik initialValues={formValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <div className="mb-3">
              <label>Label Name</label>
              <Field name="value">{({ field }) => <Input {...field} placeholder="Enter label name" />}</Field>
              <div className="validation-error">
                <ErrorMessage name="value" />
              </div>
            </div>

            <div className="mb-3">
              <label>Label Type</label>
              <Select value={values.tag_type} disabled={!!item?.id || !!defaultTagType} placeholder="Select label type" style={{ width: "100%" }} onChange={(val) => setFieldValue("tag_type", val)}>
                <Option value={2}>Order Label</Option>
                <Option value={1}>Product Label</Option>
              </Select>
              <div className="validation-error">
                <ErrorMessage name="tag_type" />
              </div>
            </div>

            {values.tag_type === 1 && <div className="mb-3">
              <label className="d-block">Display product labels on</label>
              <Checkbox checked={values.show_on_fulfillment} onChange={(event) => setFieldValue("show_on_fulfillment", event.target.checked)}>Fulfillment</Checkbox>
              <Checkbox checked={values.show_on_invoice} onChange={(event) => setFieldValue("show_on_invoice", event.target.checked)}>Invoice</Checkbox>
            </div>}
            <div className="mb-3" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Switch checked={values.status} onChange={(val) => setFieldValue("status", val)} />
              <span>{values.status ? "Active" : "Inactive"}</span>
            </div>
            <div className="d-flex" style={{ gap: "8px" }}>
              <div className="mb-3" style={{ width: "40%" }}>
                <label>background Color</label>

                <Popover
                  trigger="click"
                  placement="bottomLeft"
                  open={BgColorPickerOpen}
                  onOpenChange={setBgColorPickerOpen}
                  overlayClassName="tags-color-input-popover"
                  content={
                    <ReactGPicker
                      value={values.bg_color}
                      format="hex"
                      showAlpha={false}
                      showInputs={true}
                      showHistory={true}
                      onChange={(c) => setFieldValue("bg_color", c)}
                    />
                  }
                >
                  {/* Field with preview + hex */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      padding: "6px 10px",
                      cursor: "pointer",
                      width: "100%",
                      height: 40,
                      background: "#fff",
                    }}
                  >
                    {/* Color preview circle */}
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: values.bg_color,
                        border: "1px solid #ccc",
                      }}
                    />

                    {/* Hex text */}
                    <span style={{ fontSize: 14 }}>{values.bg_color}</span>
                  </div>
                </Popover>
              </div>

              <div className="mb-3" style={{ width: "40%" }}>
                <label>Text Color</label>

                <Popover
                  trigger="click"
                  placement="bottomLeft"
                  open={colorPickerOpen}
                  onOpenChange={setColorPickerOpen}
                  overlayClassName="tags-color-input-popover"
                  content={
                    <ReactGPicker
                      value={values.color}
                      format="hex"
                      showAlpha={false}
                      showInputs={true}
                      showHistory={true}
                      onChange={(c) => setFieldValue("color", c)}
                    />
                  }
                >
                  {/* Field with preview + hex */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      padding: "6px 10px",
                      cursor: "pointer",
                      width: "100%",
                      height: 40,
                      background: "#fff",
                    }}
                  >
                    {/* Color preview circle */}
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: values.color,
                        border: "1px solid #ccc",
                      }}
                    />

                    {/* Hex text */}
                    <span style={{ fontSize: 14 }}>{values.color}</span>
                  </div>
                </Popover>
              </div>
            </div>

            <div className="mb-3">
              <label>Description</label>
              <Field name="description">{({ field }) => <TextArea {...field} rows={3} placeholder="Description" />}</Field>
              <div className="error-text">
                <ErrorMessage name="description" />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: "16px" }}>
              <button type="button" onClick={onClose} className="action-button" style={{ backgroundColor: "#F2F2F2", color: "#333333" }}>
                Cancel
              </button>
              <button
                htmlType="submit"
                loading={isSubmitting}
                className="action-button"
                style={{
                  backgroundColor: isSubmitting ? "#CCC" : "#EA1E51",
                  color: "#FFFFFF",
                }}
                disabled={isSubmitting}
              >
                {item?.id ? "Update" : "Create"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CreateTags;
